import React, { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { Stack, StackItem } from '@fluentui/react';
import { Info20Filled, Eye20Filled, EyeOff20Filled, Checkmark16Filled, Dismiss16Filled } from "@fluentui/react-icons";
import { Button, Input, FluentProvider, webLightTheme, LargeTitle, Body1, Body1Stronger, Image, Caption1Strong, Badge, Tooltip, Text, Body1Strong, Spinner, Body2 } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { ToastComponent } from '../../components/toast/toast.component';
import { SkeletonNewPassword } from '../../components/skeleton/skeletonNewPassword';
import { backgroundPosition, formPositions, primaryIcon, signLogo, verifyotpFormPosition } from '../../styles/customStyles';
import Session from 'supertokens-web-js/recipe/session';
import client from '../../api/apollo';
import { userProfileUpdateQuery } from '../../graphql/users_queries';
import { GlobalContext } from '../../hooks/globalContext';
import { Activity_log_func } from '../../api/activity_log/activity_log_func';
import AppConfig from '../../utility/AppConfig';
import apiCall from '../../utility/api';
import { SkeletonChangePassword } from '../../components/skeleton/skeletonChangePassword';

interface User {
	[x: string]: ReactNode;
	currentPassword: string;
	newPassword: string;
	reenteredPassword: string;
}
interface ChangePasswordProps {
	overlayopen: boolean;
	setOverlayOpen: any
}


export const ChangePasswordForm: FC<ChangePasswordProps> = ({ overlayopen, setOverlayOpen }) => {
	const navigate = useNavigate();
	const [user, setUser] = useState<User>({ currentPassword: '', newPassword: '', reenteredPassword: '' });
	const [passwordResetMailSent, setPasswordResetMailSent] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [messagebar2, setMessagebar2] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [passwordFocused, setPasswordFocused] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
	const [currentPasswordFocused, setCurrentPasswordFocused] = useState(false);
	const [pswStrengthFlag, setPswStrengthFlag] = useState(true);
	const contextValue: any = useContext(GlobalContext);
	const [tracker, setTracker] = useState(false);
	const [disable, setDisabled] = useState(false);
	const [successMsg,setSuccessMsg]: any = useState('');

	useEffect(() => {
		localStorage.clear();
		const checkConnection = () => {
			const hasInternetConnection = navigator.onLine;
			setIsOnline(hasInternetConnection);
		};
		checkConnection();
		window.addEventListener('online', checkConnection);
		window.addEventListener('offline', checkConnection);
		return () => {
			window.removeEventListener('online', checkConnection);
			window.removeEventListener('offline', checkConnection);
		};
	}, []);

	const handleCurrentPasswordFocus = () => { setCurrentPasswordFocused(true); };
	const handlePasswordFocus = () => { setPasswordFocused(true); };
	const handleConfirmPasswordFocus = () => { setConfirmPasswordFocused(true); };
	const handleToggleCurrentPassword = () => {
		setShowCurrentPassword(!showCurrentPassword);
	};
	const handleToggleNewPassword = () => {
		setShowNewPassword(!showNewPassword);
	};
	const handleToggleConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const checkPasswordMatch = (user: User) => {
		const { newPassword, reenteredPassword } = user;
		if (newPassword === reenteredPassword && newPassword !== "" && reenteredPassword !== "") {
			return (
				<Badge appearance="ghost" color="success" icon={<Checkmark16Filled />} iconPosition='after'>
					<Caption1Strong>Matches</Caption1Strong>
				</Badge>
			);
		} else if ((newPassword === "" || reenteredPassword === "") && (newPassword !== reenteredPassword)) {
			return null;
		} else {
			return (
				<Badge appearance="ghost" color="danger" icon={<Dismiss16Filled />} iconPosition='after'>
					<Caption1Strong>Does not match</Caption1Strong>
				</Badge>
			);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setUser((prevUser) => ({
			...prevUser,
			[name]: value.trim(),
			passwordStrength: name === 'newPassword' ? checkPasswordStrength(value.trim()) : prevUser.passwordStrength,
			passwordMatch: name === 'reenteredPassword' || name === 'newPassword' ? checkPasswordMatch({ ...prevUser, [name]: value.trim() }) : prevUser.passwordMatch,
		}));
	};

	const checkPasswordStrength = (password: string) => {
		const strength = [/[A-Z]/.test(password), /[\W_]/.test(password),
		(password.match(/\d/g) || []).length >= 3, password.length >= 8].reduce((acc, condition) => acc + (condition ? 1 : 0), 0);
		setPswStrengthFlag(false);
		if (strength === 0) return (<Badge appearance="ghost" color="severe"> <Caption1Strong>Very Weak</Caption1Strong> </Badge>)
		if (strength === 1) return (<Badge appearance="ghost" color="danger"> <Caption1Strong>Weak</Caption1Strong> </Badge>)
		if (strength === 2) return (<Badge appearance="ghost" color="warning"> <Caption1Strong>Moderate</Caption1Strong> </Badge>);
		if (strength === 3) return (<Badge appearance="ghost" color="success"> <Caption1Strong>Strong</Caption1Strong> </Badge>);
		setPswStrengthFlag(true);
		return (<Badge appearance="ghost" color="success"> <Caption1Strong>Very Strong</Caption1Strong> </Badge>);
	};

	const handleChangePassword = async () => {
		setDisabled(true);

		if (user.newPassword.length === 0 || user.reenteredPassword.length === 0 || user.currentPassword.length === 0) {
			setDisabled(false);
			setErrorMessage("Field cannot be empty.");
			return;
		}

		if (user.newPassword.length < 8) {
			setDisabled(false);
			setErrorMessage("Password must be at least 8 characters long.");
			return;
		}
		if (user.reenteredPassword !== user.newPassword) {
			setDisabled(false);
			setErrorMessage("Passwords do not match.");
			return;
		}
		if (user.currentPassword === user.newPassword) {
			setDisabled(false);
			setErrorMessage("New password cannot be same as current password.");
			return;
		}
		try {
			// const message = await changePassword(user.currentPassword, user.newPassword);
			const response = await apiCall(`/administration/changeUserPassword`, { oldPassword: user.currentPassword, newPassword: user.newPassword });
			console.log("response",response)
			console.log('password update message: ' + response.message);
			if (response.message === "Incorrect old password") {
				setDisabled(false);
				setErrorMessage("Incorrect old password.");
			} else if (response.message === "Password updated successfully") {
				setSuccessMsg('Password updated successfully.')
				setDisabled(false);
				setMessagebar2(true);
				setTimeout(() => {
					setOverlayOpen(false);
				}, 1000);
				// userActivity("Update",`{"summary":"Password updated successfully!"}`);
			} else {
				setDisabled(false);
				setErrorMessage("Oops! Something went wrong. failed to change password");
				// userActivity("Update",`{"summary":"Password update failed"}`,'failure');
				// await Session.signOut();
				// navigate('/');
			}
		} catch (err: any) {
			setDisabled(false);
			setErrorMessage(err.message || "Password update failed");
			// userActivity("Update",`{"summary":"Password update failed"}`,'failure');
		}
	};

	useEffect(() => {
		const checkConnection = () => {
			setIsOnline(navigator.onLine);
		};

		const disableCopyPaste = (e: any) => e.preventDefault();
		const passwordInput = document.getElementById('newPassword');
		const reenteredPasswordInput = document.getElementById('reenteredPassword');

		if (passwordInput && reenteredPasswordInput) {
			passwordInput.addEventListener('copy', disableCopyPaste);
			passwordInput.addEventListener('cut', disableCopyPaste);
			passwordInput.addEventListener('paste', disableCopyPaste);
			reenteredPasswordInput.addEventListener('copy', disableCopyPaste);
			reenteredPasswordInput.addEventListener('cut', disableCopyPaste);
			reenteredPasswordInput.addEventListener('paste', disableCopyPaste);
		}

		window.addEventListener('online', checkConnection);
		window.addEventListener('offline', checkConnection);

		return () => {
			if (passwordInput && reenteredPasswordInput) {
				passwordInput.removeEventListener('copy', disableCopyPaste);
				passwordInput.removeEventListener('cut', disableCopyPaste);
				passwordInput.removeEventListener('paste', disableCopyPaste);
				reenteredPasswordInput.removeEventListener('copy', disableCopyPaste);
				reenteredPasswordInput.removeEventListener('cut', disableCopyPaste);
				reenteredPasswordInput.removeEventListener('paste', disableCopyPaste);
			}
			window.removeEventListener('online', checkConnection);
			window.removeEventListener('offline', checkConnection);
		};
	}, []);

	function hasNativePasswordToggle() {
		// Check for Microsoft Edge (Chromium-based)
		const isEdgeBrowser = /Edg(e)?\//.test(navigator.userAgent);
		
		// Specific check for Edge's native password reveal feature
		return isEdgeBrowser && 
			   'passwordReveal' in HTMLInputElement.prototype;
	  }
	  
	return (
		<>
			{isOnline
				?
				(
					<form>
						<Stack tokens={{ childrenGap: 20 }}>
							<Body1>Please set your new password.</Body1>
							<StackItem>
								<Body1Stronger>Current Password</Body1Stronger>
							</StackItem>
							<Input
								placeholder="Enter Current Password"
								type={showCurrentPassword ? "text" : "password"}
								id="currentPassword"
								name="currentPassword"
								value={user.currentPassword}
								onChange={handleInputChange}
								onFocus={handleCurrentPasswordFocus}
								autoComplete="off"
								required
								contentAfter={
									currentPasswordFocused && !hasNativePasswordToggle() && (
										<Button shape="circular"
											size="small"
											appearance="transparent"
											onClick={handleToggleCurrentPassword}
											icon={
												showCurrentPassword ? (
													<Tooltip
														withArrow
														content="Hide Password"
														relationship="label"
													>
														<EyeOff20Filled />
													</Tooltip>
												) : (
													<Tooltip
														withArrow
														content="Show Password"
														relationship="label"
													>
														<Eye20Filled />
													</Tooltip>
												)
											}
										/>
									)
								}
							/>
							<StackItem>
								<Body1Stronger>Create New password</Body1Stronger>
							</StackItem>
							<Input
								placeholder="Enter New Password"
								type={showNewPassword ? "text" : "password"}
								id="newPassword"
								name="newPassword"
								value={user.newPassword}
								onChange={handleInputChange}
								onFocus={handlePasswordFocus}
								required
								contentAfter={
									passwordFocused && !hasNativePasswordToggle() && (
										<Button shape="circular"
											size="small"
											appearance="transparent"
											onClick={handleToggleNewPassword}
											icon={
												showNewPassword ? (
													<Tooltip
														withArrow
														content="Hide Password"
														relationship="label"
													>
														<EyeOff20Filled />
													</Tooltip>
												) : (
													<Tooltip
														withArrow
														content="Show Password"
														relationship="label"
													>
														<Eye20Filled />
													</Tooltip>
												)
											}
										/>
									)
								}
							/>
							<Body1Strong>
								Password Strength: {user.passwordStrength}
							</Body1Strong>

							<StackItem>
								<Body1Stronger>Confirm new password</Body1Stronger>
							</StackItem>
							<Input
								placeholder="Re-Enter Password"
								type={showConfirmPassword ? "text" : "password"}
								id="reenteredPassword"
								name="reenteredPassword"
								value={user.reenteredPassword}
								onChange={handleInputChange}
								onFocus={handleConfirmPasswordFocus}
								contentAfter={
									confirmPasswordFocused ? (
										<Button shape="circular"
											size="small"
											appearance="transparent"
											onClick={handleToggleConfirmPassword}
											icon={
												showConfirmPassword ? (
													<Tooltip
														withArrow
														content="Hide Password"
														relationship="label"
													>
														<EyeOff20Filled />
													</Tooltip>
												) : (
													<Tooltip
														withArrow
														content="Show Password"
														relationship="label"
													>
														<Eye20Filled />
													</Tooltip>
												)
											}
										/>
									) : null
								}
							/>
							<Body1Strong>Password Status: {user.passwordMatch}</Body1Strong>

							<Stack tokens={{ childrenGap: 5 }} horizontal>
								<StackItem>
									<Info20Filled {...primaryIcon} />
								</StackItem>
								<Caption1Strong align="justify">
									Create a password with at least 8 characters. Including a
									mix of uppercase, lowercase, special characters, and at
									least 3 numbers. Avoid common words and personal
									information. Keep your account safe by choosing a unique
									password.
								</Caption1Strong>
							</Stack>

							<StackItem>
								<Text></Text>
							</StackItem>

							<StackItem>
							<Stack horizontal verticalAlign="center" tokens={{childrenGap:10}}>
								<Button shape="circular"
									type="button"
									appearance="primary"
									onClick={handleChangePassword}
									disabled={disable}
								>
									Continue
								</Button>
								{disable?<Spinner delay={0} size="extra-tiny" />: ''}
								{messagebar2 && (<Badge appearance="ghost" color="success"> <Body2> {successMsg} </Body2> </Badge>)}
								</Stack>
							</StackItem>

							{messagebar2 && (
								<ToastComponent
									setmessagebar={setMessagebar2}
									isToastEnabled={messagebar2}
									footer=""
									toastIntent={
										user.newPassword === "" ||
											user.newPassword.length < 8 ||
											user.reenteredPassword !== user.newPassword ||
											!pswStrengthFlag
											? "warning"
											: "success"
									}
									position="top-end"
									title={
										user.newPassword === "" ||
											user.newPassword.length < 8 ||
											user.reenteredPassword !== user.newPassword ||
											!pswStrengthFlag
											? "Warning"
											: "Success"
									}
									message={
										user.newPassword === "" || user.reenteredPassword === ""
											? "Field cannot be empty"
											: user.newPassword.length < 8
												? "Password must be at least 8 characters long."
												: user.reenteredPassword !== user.newPassword ||
													!pswStrengthFlag
													? "Please check the password or read the password instruction"
													: "Password reset successful!"
									}
								/>
							)}
							{errorMessage && (
								<ToastComponent
									setmessagebar={setErrorMessage}
									isToastEnabled={errorMessage}
									footer=""
									toastIntent={errorMessage ? "warning" : "success"}
									position="top-end"
									title={errorMessage ? "Warning" : "Success"}
									message={errorMessage}
								/>
							)}
						</Stack>
					</form>
				) : <SkeletonChangePassword />
			}
			{/* {tracker && <Activity_log_func data={userActivityData} setTracker={setTracker} type="insertLog" />} */}
		</>
	);
};
